import { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Box,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Block as BlockIcon,
  CardGiftcard as TrialIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { PATH_ADMIN } from '../../../routes/paths';

const BannerAlert = styled(Alert)(({ theme, severity }) => ({
  marginRight: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1.5),
  '& .MuiAlert-message': {
    padding: theme.spacing(0.5, 0),
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiAlert-action': {
    padding: 0,
    marginLeft: theme.spacing(1),
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
}));

OrgPaymentBanner.propTypes = {
  user: PropTypes.object.isRequired,
};

export default function OrgPaymentBanner({ user }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const paymentStatus = user?.org_pay;
  const hasDues = Number(user?.org_dues || 0) > 0;

  // A suspension with no open invoice is a hold placed for a non-billing
  // reason (lapsed trial, plan ended), so it gets no payment wording at all.
  const isNonBillingHold = paymentStatus === 'suspended' && !hasDues;

  // free_trial uses an external upgrade link; every other dunning state should
  // route the user to the in-app billing page and auto-open the pay dialog.
  const canPay = !isNonBillingHold && ['pending', 'overdue', 'unpaid', 'suspended'].includes(paymentStatus);

  // The DeskOS team can switch the dues reminder off for an org that pays
  // several invoices at once. Suspension still shows: the account is locked.
  const duesBannerHidden = user?.org_dues_banner === false
    && ['pending', 'overdue', 'unpaid'].includes(paymentStatus);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handlePayNow = () => {
    setDialogOpen(false);
    navigate(PATH_ADMIN.deskosBilling.root, { state: { openPay: true } });
  };

  const getStatusConfig = () => {
    switch (paymentStatus) {
      case 'pending':
        return {
          severity: 'warning',
          icon: <WarningIcon />,
          message: 'Latest Invoice Pending',
          showBanner: true,
          dialogTitle: 'Payment Reminder',
          dialogContent: (
            <>
              <Typography variant="body1" paragraph>
                Your latest invoice is currently <strong>pending</strong>.
              </Typography>
              <Typography variant="body1" paragraph>
                Please pay your invoice as soon as possible to avoid it becoming overdue. Overdue invoices may lead
                to service interruptions.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We appreciate your prompt attention to this matter.
              </Typography>
            </>
          ),
        };

      case 'overdue':
        return {
          severity: 'error',
          icon: <ErrorIcon />,
          message: 'Invoice Overdue',
          showBanner: true,
          dialogTitle: 'Urgent: Invoice Overdue',
          dialogContent: (
            <>
              <Typography variant="body1" paragraph color="error" fontWeight={600}>
                Your invoice has become <strong>OVERDUE</strong>.
              </Typography>
              <Typography variant="body1" paragraph>
                You are requested to pay the invoice as soon as possible to avoid suspension of your account.
              </Typography>
              <Typography variant="body1" paragraph>
                If the invoice is not paid soon, your users will lose access to the platform and all services will be
                temporarily suspended.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please contact us immediately if you need assistance with payment.
              </Typography>
            </>
          ),
        };

      case 'free_trial':
        return {
          severity: 'info',
          icon: <TrialIcon />,
          message: 'Free Trial Active',
          showBanner: true,
          dialogTitle: 'Free Trial Period',
          dialogContent: (
            <>
              <Typography variant="body1" paragraph>
                You are currently on a <strong>free trial</strong> of DeskOS.
              </Typography>
              <Typography variant="body1" paragraph>
                If you like what you see here, we encourage you to switch to a paid plan before your trial expires to
                continue enjoying uninterrupted access to all features.
              </Typography>
              <Typography variant="body1" paragraph>
                Visit{' '}
                <a
                  href="https://deskos.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 600 }}
                >
                  deskos.net
                </a>{' '}
                to upgrade your account today.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Thank you for trying DeskOS!
              </Typography>
            </>
          ),
        };

      case 'unpaid':
        return {
          severity: 'error',
          icon: <ErrorIcon />,
          message: 'Payment Required - Suspension Imminent',
          showBanner: true,
          dialogTitle: 'Critical: Payment Required',
          dialogContent: (
            <>
              <Typography variant="body1" paragraph color="error" fontWeight={600}>
                Your account has <strong>UNPAID</strong> invoices.
              </Typography>
              <Typography variant="body1" paragraph>
                Your account may be suspended at any time due to non-payment. Once suspended, you and your users will
                lose access to the platform immediately.
              </Typography>
              <Typography variant="body1" paragraph>
                Please pay your outstanding invoices immediately to maintain access to your account and prevent service
                interruption.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you're experiencing payment difficulties, please contact our support team immediately.
              </Typography>
            </>
          ),
        };

      case 'suspended':
        if (isNonBillingHold) {
          return {
            severity: 'error',
            icon: <BlockIcon />,
            message: 'Account On Hold',
            showBanner: true,
            dialogTitle: 'Account On Hold',
            dialogContent: (
              <>
                <Typography variant="body1" paragraph color="error" fontWeight={600}>
                  Your workspace has been placed <strong>ON HOLD</strong>.
                </Typography>
                <Typography variant="body1" paragraph>
                  Access is paused for you and your members. There are no outstanding invoices, so this is not a
                  payment matter.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please contact the DeskOS team at support@deskos.net to have access restored.
                </Typography>
              </>
            ),
          };
        }
        return {
          severity: 'error',
          icon: <BlockIcon />,
          message: 'Account Suspended',
          showBanner: true,
          dialogTitle: 'Account Suspended',
          dialogContent: (
            <>
              <Typography variant="body1" paragraph color="error" fontWeight={600}>
                Your account has been <strong>SUSPENDED</strong>.
              </Typography>
              <Typography variant="body1" paragraph>
                You have lost access to the platform due to non-payment of outstanding invoices.
              </Typography>
              <Typography variant="body1" paragraph>
                To restore access to your account and continue the service, please pay your dues immediately.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once payment is received, your account will be reactivated and all services will be restored.
              </Typography>
            </>
          ),
        };

      case 'paid':
      default:
        return {
          showBanner: false,
        };
    }
  };

  const statusConfig = getStatusConfig();

  // Don't render anything if status is 'paid' or not defined
  if (!statusConfig.showBanner || duesBannerHidden) {
    return null;
  }

  return (
    <>
      {/* Banner Display */}
      <BannerAlert
        severity={statusConfig.severity}
        icon={statusConfig.icon}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            {canPay && (
              <Button
                size="small"
                variant="contained"
                color={statusConfig.severity === 'warning' ? 'warning' : 'error'}
                onClick={handlePayNow}
                sx={{ fontWeight: 700, py: 0.25, whiteSpace: 'nowrap' }}
              >
                Pay Now
              </Button>
            )}
            <IconButton size="small" color="inherit" onClick={handleOpenDialog}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
        sx={{pt:0.5,pb:0.5}}
      >
        <Typography variant="body2" fontWeight={600}>
          {statusConfig.message}
        </Typography>
      </BannerAlert>

      {/* Dialog for More Information */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1,
          }}
        >
          {statusConfig.icon}
          {statusConfig.dialogTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>{statusConfig.dialogContent}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined" color="inherit">
            Close
          </Button>
          {canPay && (
            <Button onClick={handlePayNow} variant="contained" color="primary">
              Pay Now
            </Button>
          )}
          {paymentStatus === 'free_trial' && (
            <Button
              onClick={() => window.open('https://deskos.net', '_blank')}
              variant="outlined"
              color="primary"
            >
              Upgrade Now
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}